<div class="project-dialog">
  <div class="dialog-header">
    <h3>{{ title }}</h3>
    <button type="button" class="close-btn" (click)="cancel()" [disabled]="suggestBrandDetailsLoading" [attr.aria-disabled]="suggestBrandDetailsLoading" [title]="suggestBrandDetailsLoading ? 'Verifying brand details...' : 'Close'" aria-label="Close">&times;</button>
  </div>

  <div class="dialog-body">
    @if (mode === 'edit' && !project) {
      <div class="loading-container">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Loading project details...</p>
      </div>
    }

    <!-- Edit mode: single form (no stepper) -->
    @if (mode === 'edit' && project) {
      <form (ngSubmit)="submit()">
        <section class="form-section">
          <h4 class="section-title">Brand</h4>
          <div class="form-row form-row-2">
            <mat-form-field appearance="outline" class="form-group form-group-full">
              <mat-label>Name</mat-label>
              <input matInput id="edit_name" [(ngModel)]="form.name" name="name" required disabled>
            </mat-form-field>
            <mat-form-field appearance="outline" class="form-group form-group-full">
              <mat-label>Domain</mat-label>
              <input matInput id="edit_description" [(ngModel)]="form.description" name="description" placeholder="e.g. meritas.co.uk" required disabled>
            </mat-form-field>
          </div>
            <div class="form-row form-row-2">
            <mat-form-field appearance="outline" class="form-group form-group-full">
              <mat-label>Country</mat-label>
              <mat-select id="edit_country" [(ngModel)]="form.primary_country" name="primary_country" required>
                @for (country of countryOptions; track country) {
                  <mat-option [value]="country">{{ country }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="form-group form-group-full">
              <mat-label>Language</mat-label>
              <mat-select id="edit_language" [(ngModel)]="form.language" name="language">
                @for (opt of languageOptions; track opt.code) {
                  <mat-option [value]="opt.label">{{ opt.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          @if (form.industries.length || form.categories.length) {
            <div class="form-row form-row-2">
              <mat-form-field appearance="outline" class="form-group form-group-full">
                <mat-label>Primary Industry/ies</mat-label>
                <mat-select [(ngModel)]="form.selectedIndustryNames" name="edit_industries" multiple>
                  @for (ind of form.industries; track ind.name) {
                    <mat-option [value]="ind.name">{{ ind.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="form-group form-group-full">
                <mat-label>Primary Category</mat-label>
                <mat-select
                  [ngModel]="form.selectedCategoryNames[0]"
                  name="edit_categories"
                  [disabled]="true">
                  @for (cat of form.categories; track cat.name) {
                    <mat-option [value]="cat.name">{{ cat.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          }
        </section>
        <section class="form-section">
          <div class="section-header">
            <h4 class="section-title">Competitors</h4>
          </div>
          <p class="form-hint">Optional. Max {{ MAX_COMPETITORS }} competitors.</p>
          @for (comp of form.competitors; track $index) {
            <div class="competitor-row">
              <input type="text" [(ngModel)]="comp.name" [name]="'edit_comp_name_' + $index" placeholder="Competitor name" class="competitor-input">
              <input type="text" [(ngModel)]="comp.url" [name]="'edit_comp_url_' + $index" placeholder="www.branddomain.com" class="competitor-input">
              <button type="button" class="btn btn-sm btn-danger competitor-remove" (click)="removeCompetitor($index)" title="Remove">−</button>
            </div>
          }
        </section>
        <div class="dialog-actions">
          <button type="button" class="btn btn-secondary" (click)="cancel()">Cancel</button>
          <button type="submit" class="btn btn-primary" [disabled]="saving">{{ submitLabel }}</button>
        </div>
      </form>
    }

    <!-- Create mode: 3-step stepper (linear = strict sequential; completed = step enabled for next) -->
    @if (mode === 'create') {
      <mat-stepper linear #stepper>
        <!-- Step 1: Brand name & domain -->
        <mat-step [completed]="step1Completed">
          <ng-template matStepLabel>Brand Name & Domain</ng-template>
          <section class="form-section mt-10">
            <div class="form-row form-row-2">
              <mat-form-field appearance="outline" class="form-group form-group-full">
                <mat-label>Brand name</mat-label>
                <input matInput id="dialog_name" [(ngModel)]="form.name" name="name" placeholder="Enter brand name" required>
              </mat-form-field>
              <mat-form-field appearance="outline" class="form-group form-group-full">
                <mat-label>Brand domain</mat-label>
                <input matInput id="dialog_description" [(ngModel)]="form.description" name="description" placeholder="www.branddomain.com" required>
              </mat-form-field>
            </div>
          </section>
          <div class="stepper-actions">
            <button type="button" class="btn btn-secondary" (click)="cancel()" [disabled]="suggestBrandDetailsLoading">Cancel</button>
            <button type="button" class="btn btn-primary btn-next" (click)="onStep1Next(stepper)" [disabled]="!form.name.trim() || !form.description.trim()">
              Next
            </button>
          </div>
        </mat-step>

        <!-- Step 2: Market & language – click Verify to go to Step 3 and run verification there -->
        <mat-step [completed]="step2Completed">
          <ng-template matStepLabel>Market &amp; language</ng-template>
          <section class="form-section">
            <p class="form-hint step2-hint">Verify brand details in the next step after clicking Verify below.</p>
            <div class="form-row form-row-2">
              <mat-form-field appearance="outline" class="form-group form-group-full">
                <mat-label>Market (country)</mat-label>
                <mat-select id="dialog_country" [(ngModel)]="form.primary_country" name="primary_country" required>
                  @for (country of countryOptions; track country) {
                    <mat-option [value]="country">{{ country }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="form-group form-group-full">
                <mat-label>Language</mat-label>
                <mat-select id="dialog_language" [(ngModel)]="form.language" name="language">
                  @for (opt of languageOptions; track opt.code) {
                    <mat-option [value]="opt.label">{{ opt.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </section>
          <div class="stepper-actions">
            <button type="button" class="btn btn-secondary" matStepperPrevious>Back</button>
            <button type="button" class="btn btn-primary btn-next" (click)="onStep2Next(stepper)" [disabled]="suggestBrandDetailsLoading">
              {{ suggestBrandDetailsLoading ? 'Verifying...' : (suggestBrandDetailsResult ? 'Re-verify' : 'Verify') }}
            </button>
          </div>
        </mat-step>

        <!-- Step 3: Verify results – loading (spinner) shown here while API runs, then industry & category -->
        <mat-step>
          <ng-template matStepLabel>Industry &amp; Category</ng-template>
          <section class="form-section">
            @if (suggestBrandDetailsLoading) {
              <div class="loading-inline">
                <mat-spinner diameter="32"></mat-spinner>
                <span>Verifying brand Industry & Category...</span>
              </div>
            }
            @if (!suggestBrandDetailsLoading && !suggestBrandDetailsResult) {
              <p class="form-hint">Complete Step 2 and click Next to load suggestions here.</p>
            }
            @if (!suggestBrandDetailsLoading && suggestBrandDetailsResult) {
              @if (suggestBrandDetailsResult.error) {
                <p class="form-hint error-hint">{{ suggestBrandDetailsResult.error }}</p>
                <button type="button" class="btn btn-secondary" (click)="verifyAndExplore()" [disabled]="suggestBrandDetailsLoading">Re-verify</button>
              }               @else if (form.industries.length && form.categories.length) {
                <h4 class="section-title">Suggested industry &amp; category</h4>
                <div class="form-row form-row-2">
                  <mat-form-field appearance="outline" class="form-group form-group-full">
                    <mat-label>Primary Industry/ies</mat-label>
                    <mat-select [(ngModel)]="form.selectedIndustryNames" name="dialog_industries" multiple>
                      @for (ind of form.industries; track ind.name) {
                        <mat-option [value]="ind.name">{{ ind.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="form-group form-group-full">
                    <mat-label>Categories for analysis (max {{ MAX_ANALYSIS_CATEGORIES }})</mat-label>
                    <mat-select
                      [ngModel]="form.selectedCategoryNames"
                      (ngModelChange)="onCategoriesChange($event)"
                      name="dialog_categories"
                      multiple>
                      @for (cat of form.categories; track cat.name) {
                        <mat-option [value]="cat.name">{{ cat.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                <p class="form-hint">Choose up to {{ MAX_ANALYSIS_CATEGORIES }} categories for analysis. If you pick fewer, we add related suggestions until {{ MAX_ANALYSIS_CATEGORIES }}. Re-verify to refresh the list.</p>
                <button type="button" class="btn btn-secondary btn-reverify" (click)="verifyAndExplore()" [disabled]="suggestBrandDetailsLoading">
                  Re-verify
                </button>
              }
            }
          </section>
          <!-- <div class="stepper-actions">
            <button type="button" class="btn btn-secondary" matStepperPrevious>Back</button>
            <button type="button" class="btn btn-primary btn-next" (click)="onStep3Next(stepper)" [disabled]="!(form.selectedIndustryNames.length && form.selectedCategoryNames.length)">Next</button>
          </div>
        </mat-step>

        <mat-step>
          <ng-template matStepLabel>Competitors</ng-template>
          <section class="form-section">
            <div class="section-header">
              <h4 class="section-title">Competitors</h4>
              <button type="button" class="btn btn-sm btn-secondary" (click)="addCompetitor()"
                [disabled]="form.competitors.length >= MAX_COMPETITORS" title="Add competitor (max {{ MAX_COMPETITORS }})">
                +
              </button>
            </div>
            @for (comp of form.competitors; track $index) {
              <div class="competitor-row">
                <input type="text" [(ngModel)]="comp.name" [name]="'comp_name_' + $index" placeholder="Competitor name" class="competitor-input">
                <input type="text" [(ngModel)]="comp.url" [name]="'comp_url_' + $index" placeholder="www.branddomain.com" class="competitor-input">
                <button type="button" class="btn btn-sm btn-danger competitor-remove" (click)="removeCompetitor($index)" title="Remove">−</button>
              </div>
            }
          </section> -->
          <div class="dialog-actions dialog-actions-final">
            <button type="button" class="btn btn-secondary" matStepperPrevious>Back</button>
            <button
              type="button"
              class="btn btn-primary"
              (click)="submit(false)"
              [disabled]="saving || !(form.selectedIndustryNames.length && form.selectedCategoryNames.length)">
              Create
            </button>
            <button
              type="button"
              class="btn btn-create-analyze"
              (click)="submit(true)"
              [disabled]="saving || !(form.selectedIndustryNames.length && form.selectedCategoryNames.length)">
              {{ saving ? 'Creating...' : 'Create &amp; Analysis' }}
            </button>
          </div>
        </mat-step>
      </mat-stepper>
    }
  </div>
</div>
